import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Package, Plus, Edit2, Trash2, Save, X, AlertTriangle } from 'lucide-react';
import { inventory as inventoryApi } from '../utils/api';

const InventoryManagement = () => {
  const [inventory, setInventory] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    item_name: '',
    description: '',
    category: '',
    quantity: 0,
    unit: '',
    cost_per_unit: 0,
    supplier: '',
    reorder_level: 0,
    notes: '',
  });

  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = async () => {
    setLoading(true);
    try {
      const [inventoryRes, lowStockRes] = await Promise.all([
        inventoryApi.getAll(),
        inventoryApi.getLowStock(),
      ]);

      setInventory(inventoryRes.data);
      setLowStockItems(lowStockRes.data);
    } catch (error) {
      console.error('Load inventory error:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        item_name: item.item_name,
        description: item.description || '',
        category: item.category || '',
        quantity: item.quantity,
        unit: item.unit || '',
        cost_per_unit: item.cost_per_unit || 0,
        supplier: item.supplier || '',
        reorder_level: item.reorder_level || 0,
        notes: item.notes || '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        item_name: '',
        description: '',
        category: '',
        quantity: 0,
        unit: '',
        cost_per_unit: 0,
        supplier: '',
        reorder_level: 0,
        notes: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.item_name.trim()) {
      toast.error('Item name is required');
      return;
    }

    try {
      if (editingItem) {
        await inventoryApi.update(editingItem.id, formData);
        toast.success('Item updated successfully');
      } else {
        await inventoryApi.create(formData);
        toast.success('Item added successfully');
      }

      handleCloseModal();
      loadInventory();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await inventoryApi.delete(id);
      toast.success('Item deleted successfully');
      loadInventory();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete item');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <Package size={32} className="text-primary-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Inventory Management</h1>
              <p className="text-gray-600">Track your grooming supplies and products</p>
            </div>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Add Item</span>
          </button>
        </div>

        {/* Low Stock Alerts */}
        {lowStockItems.length > 0 && (
          <div className="card bg-yellow-50 border-2 border-yellow-200 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle size={24} className="text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-2">Low Stock Alert</h3>
                <p className="text-sm text-yellow-800 mb-3">
                  {lowStockItems.length} item(s) are running low on stock
                </p>
                <div className="space-y-2">
                  {lowStockItems.map((item) => (
                    <div key={item.id} className="text-sm text-yellow-900">
                      <strong>{item.item_name}</strong>: {item.quantity} {item.unit} remaining
                      (reorder at {item.reorder_level})
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Inventory Table */}
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Inventory Items</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4">Item Name</th>
                  <th className="text-left py-3 px-4">Category</th>
                  <th className="text-right py-3 px-4">In Stock</th>
                  <th className="text-left py-3 px-4">Unit</th>
                  <th className="text-right py-3 px-4">Cost/Unit</th>
                  <th className="text-right py-3 px-4">Total Value</th>
                  <th className="text-left py-3 px-4">Notes</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium">{item.item_name}</p>
                        {item.description && (
                          <p className="text-sm text-gray-600">{item.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                        {item.category}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4">
                      <span className={item.quantity <= item.reorder_level ? 'text-red-600 font-semibold' : ''}>
                        {item.quantity}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-gray-600">
                        {item.unit || '—'}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4">{formatCurrency(item.cost_per_unit)}</td>
                    <td className="text-right py-3 px-4 font-semibold">
                      {formatCurrency(item.quantity * item.cost_per_unit)}
                    </td>
                    <td className="py-3 px-4">
                      {item.notes ? (
                        <p className="text-sm text-gray-600 max-w-xs truncate" title={item.notes}>
                          {item.notes}
                        </p>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="text-right py-3 px-4">
                      <button
                        onClick={() => handleOpenModal(item)}
                        className="text-blue-600 hover:text-blue-700 mr-3"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">
                    {editingItem ? 'Edit Item' : 'Add New Item'}
                  </h2>
                  <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Item Name *</label>
                      <input
                        type="text"
                        value={formData.item_name}
                        onChange={(e) => handleInputChange('item_name', e.target.value)}
                        className="input"
                        placeholder="e.g., Dog Shampoo"
                      />
                    </div>

                    <div>
                      <label className="label">Category</label>
                      <input
                        type="text"
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="input"
                        placeholder="e.g., shampoo, tools, treats"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="input"
                      rows="2"
                      placeholder="Brief description of the item"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Quantity In Stock *</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.quantity}
                        onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 0)}
                        className="input"
                        placeholder="e.g., 50"
                      />
                      <p className="text-xs text-gray-500 mt-1">How many do you currently have?</p>
                    </div>

                    <div>
                      <label className="label">Unit Type</label>
                      <input
                        type="text"
                        value={formData.unit}
                        onChange={(e) => handleInputChange('unit', e.target.value)}
                        className="input"
                        placeholder="e.g., bottles, boxes, pieces"
                      />
                      <p className="text-xs text-gray-500 mt-1">What is each item measured in?</p>
                    </div>

                    <div>
                      <label className="label">Cost per Unit</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.cost_per_unit}
                        onChange={(e) => handleInputChange('cost_per_unit', parseFloat(e.target.value) || 0)}
                        className="input"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Supplier</label>
                      <input
                        type="text"
                        value={formData.supplier}
                        onChange={(e) => handleInputChange('supplier', e.target.value)}
                        className="input"
                        placeholder="Supplier name"
                      />
                    </div>

                    <div>
                      <label className="label">Reorder Level</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.reorder_level}
                        onChange={(e) => handleInputChange('reorder_level', parseInt(e.target.value) || 0)}
                        className="input"
                        placeholder="Alert when quantity falls below"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      className="input"
                      rows="2"
                      placeholder="Additional notes"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button onClick={handleCloseModal} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button onClick={handleSave} className="btn btn-primary flex items-center space-x-2">
                    <Save size={18} />
                    <span>{editingItem ? 'Update' : 'Add'} Item</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryManagement;
